import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Stack, Typography, Box, useTheme, IconButton } from '@mui/material';
import dayjs from 'dayjs';

import { useRequestState } from '@app/hooks';
import { CustomAvatar, Iconify } from '@app/components';
import { Strings } from '@app/constants';
import { IDeleteCardSchema } from '../types';
import { getTransaction } from '../slice';
import { selectTransactionState } from '../selector';

interface Props {
  ids: IDeleteCardSchema;
}

const Transactions = ({ ids }: Props) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  useEffect(() => {
    if (ids?.cardId) {
      dispatch(getTransaction({ cardId: ids.cardId }));
    }
  }, [dispatch, ids?.cardId]);

  const { data: transactionsData } = useRequestState({
    stateSelector: selectTransactionState,
    errorShown: true,
  });

  const formatTimestamp = (timestamp: string) => {
    const date = dayjs(timestamp);
    if (date.isSame(dayjs(), 'day')) {
      return date.fromNow();
    }
    return date.format('D MMM YYYY, h:mm A');
  };

  const renderMessageWithBold = (message: string) => {
    const regex = /(['"])(.*?)\1/g;
    const parts: Array<string | { bold: boolean; text: string }> = [];
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(message)) !== null) {
      if (match.index > lastIndex) {
        parts.push(message.slice(lastIndex, match.index));
      }
      parts.push({ bold: true, text: match[2] });
      lastIndex = regex.lastIndex;
    }

    if (lastIndex < message.length) {
      parts.push(message.slice(lastIndex));
    }

    return parts.map((part, index) =>
      typeof part === 'string' ? (
        <span key={index}>{part}</span>
      ) : (
        <Box key={index} component="span" sx={{ fontWeight: 'bold', display: 'inline' }}>
          {part.text}
        </Box>
      )
    );
  };

  if (!transactionsData || transactionsData.length === 0) {
    return null;
  }

  return (
    <Stack ml={2} my={2}>
      <Stack
        sx={{
          alignItems: 'center',
          flexDirection: 'row',
          ml: -0.8,
          my: 0.5,
        }}
      >
        <IconButton
          aria-label="close"
          sx={{
            color: theme.palette.taskboard.deepPurple,
          }}
        >
          <Iconify icon={'fluent:text-description-32-filled'} />
        </IconButton>
        <Typography sx={{ fontWeight: 500, color: theme.palette.grey['800'] }}>
          {Strings.taskBoard.transactionHistory}
        </Typography>
      </Stack>
      {transactionsData?.map((item: any, index: number) => {
        const firstName: string = typeof item?.firstName === 'string' ? item?.firstName : '';
        const lastName: string = typeof item?.lastName === 'string' ? item?.lastName : '';
        const name = `${firstName} ${lastName}`.trim();

        return (
          <Box key={index} sx={{ my: 0.7 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CustomAvatar
                name={name}
                src={item?.profileImage}
                style={{
                  backgroundColor: theme.palette.taskboard.raisinBlack,
                  color: theme.palette.common.white,
                  width: '35px',
                  height: '35px',
                  fontSize: '12px',
                }}
              />

              <Box flex={1}>
                <Typography
                  sx={{
                    color: theme.palette.grey[700],
                    fontSize: '0.95rem',
                  }}
                >
                  {renderMessageWithBold(item.message)}
                </Typography>

                <Typography
                  sx={{
                    color: theme.palette.grey[600],
                    fontSize: '0.8rem',
                  }}
                >
                  {formatTimestamp(item.timestamp)}
                </Typography>
              </Box>
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
};

export { Transactions };
